import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { Permissao } from '@nexital/shared';
import { useDependencias, useEnvio, useQuadro } from '../../application/dependencias';
import { Acompanhamento } from '../notas/Acompanhamento';
import { AvisoNotas } from '../notas/AvisoNotas';
import { ProvedorNotas } from '../notas/contexto-notas';
import '../notas/notas.css';
import { TelaLicitacao } from '../licitacao/TelaLicitacao';
import { BarraSuperior } from '../shell/BarraSuperior';
import { ProvedorAcoesEstado } from './AcoesEstado';
import { BarraFerramentas, type Ferramenta } from './BarraFerramentas';
import { Cabecalho } from './Cabecalho';
import { Lixeira } from './Lixeira';
import { PainelLixeira } from './PainelLixeira';
import { Presenca } from './Presenca';
import { uuidDoNo, type NoQuadro } from './montar-nos';
import { Quadro } from './Quadro';
import { SemEstado } from './SemEstado';
import { ArquivosSemProcesso } from './ArquivosSemProcesso';
import { TelaConfiguracoes } from '../configuracoes/TelaConfiguracoes';
import { ProvedorConfirmacao } from '../design-system/Confirmacao';
import { AvisoSistema } from '../shell/AvisoSistema';
import { DialogoAprovarConvite } from '../shell/DialogoAprovarConvite';
import { DialogoEnviarArquivos } from '../shell/DialogoEnviarArquivos';
import { useTempoReal } from './use-tempo-real';

const TECLAS: Record<string, Ferramenta> = { s: 'selecao', d: 'mao', n: 'nota', c: 'conector' };

/** Atalhos: S seleção, D mover, N post-it, C seta, Shift+1 ajustar, Enter abre o cartão (RQ-QDR-003 a RQ-QDR-005). */
function useAtalhos(definir: (f: Ferramenta) => void, abrir: (processoUuid: string) => void) {
  const { fitView, getNodes } = useReactFlow<NoQuadro>();
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable], dialog')) return;
      const ferramenta = !e.ctrlKey && !e.metaKey ? TECLAS[e.key.toLowerCase()] : undefined;
      if (ferramenta) definir(ferramenta);
      if (e.key === 'Escape') definir('selecao');
      if (e.shiftKey && e.code === 'Digit1') void fitView({ duration: 300, padding: 0.08 });
      const selecionado = e.key === 'Enter' ? getNodes().find((n) => n.selected && n.type === 'cartao') : undefined;
      if (selecionado) abrir(uuidDoNo(selecionado.id));
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [definir, fitView, getNodes, abrir]);
}

/** Convite a aprovar: pelo aviso no sino ou pelo link do e-mail (?convite=uuid, tirado da URL ao abrir). */
function useConviteDaUrl() {
  const { storeEnvio } = useDependencias();
  const convite = useEnvio((s) => s.convite);
  useEffect(() => {
    const url = new URL(window.location.href);
    const uuid = url.searchParams.get('convite');
    if (!uuid) return;
    storeEnvio.getState().abrirConvite(uuid);
    url.searchParams.delete('convite');
    window.history.replaceState(null, '', url.pathname + url.search);
  }, [storeEnvio]);
  return convite;
}

/** Carrega as notas e setas do quadro e informa ao store quem está no quadro e o que a pessoa pode fazer. */
function useNotasDoQuadro() {
  const { storeNotas } = useDependencias();
  const membros = useQuadro((s) => s.dados?.membros);
  const permissoes = useQuadro((s) => s.dados?.permissoes);
  useEffect(() => {
    void storeNotas.getState().carregar();
  }, [storeNotas]);
  useEffect(() => {
    if (!membros || !permissoes) return;
    storeNotas.getState().configurar({ pessoasQuadro: membros, podeTornarPublica: permissoes.includes(Permissao.CriarNotasPublicas) });
  }, [storeNotas, membros, permissoes]);
}

/** Lixeira (o painel abre para cima) e, ao lado, as bandejas Sem estado e Arquivos sem processo. */
function CantoInferior({ lixeira, lixeiraAberta }: { lixeira: React.RefObject<HTMLButtonElement | null>; lixeiraAberta: boolean }) {
  const quantidade = useQuadro((s) => s.dados?.lixeira.length ?? 0);
  const [painel, definirPainel] = useState(false);
  const fecharPainel = () => {
    definirPainel(false);
    lixeira.current?.focus();
  };
  return (
    <div className="canto-inferior-esquerdo">
      <div className="lixeira-area">
        {painel && <PainelLixeira fechar={fecharPainel} />}
        <Lixeira ref={lixeira} aberta={lixeiraAberta} quantidade={quantidade} expandida={painel} aoClicar={() => (painel ? fecharPainel() : definirPainel(true))} />
      </div>
      <SemEstado />
      <ArquivosSemProcesso />
    </div>
  );
}

function Conteudo({ usuario }: { usuario: { nome: string; uuid: string } }) {
  const [ferramenta, definirFerramenta] = useState<Ferramenta>('selecao');
  const [lixeiraAberta, abrirLixeira] = useState(false);
  const dialogoEnvio = useEnvio((s) => s.dialogo);
  const convite = useConviteDaUrl();
  const configuracoesAbertas = useEnvio((s) => s.configuracoes !== null);
  const lixeira = useRef<HTMLButtonElement>(null);
  const dados = useQuadro((s) => s.dados);
  const { espacos } = useDependencias();
  const { empresaUuid, quadroUuid, processoUuid } = espacos.atual;
  const { navegar } = espacos;
  const abrir = useCallback((uuid: string) => navegar({ empresaUuid, quadroUuid, processoUuid: uuid }), [navegar, empresaUuid, quadroUuid]);
  const fechar = useCallback(() => navegar({ empresaUuid, quadroUuid }), [navegar, empresaUuid, quadroUuid]);
  useAtalhos(definirFerramenta, abrir);
  useTempoReal();
  useNotasDoQuadro();

  return (
    <ProvedorConfirmacao>
    <ProvedorAcoesEstado>
      <div className="tela-quadro">
        {/* Com a licitação aberta, o quadro e a barra ficam atrás da janela: sem clique, foco nem busca. */}
        <div className="tela-quadro__fundo" inert={Boolean(processoUuid) || configuracoesAbertas}>
        <BarraSuperior espaco={dados?.quadro.nome ?? 'Meu espaço'} usuario={usuario} />
        <Quadro ferramenta={ferramenta} definirFerramenta={definirFerramenta} lixeira={lixeira} abrirLixeira={abrirLixeira} abrirProcesso={abrir} />
        <Cabecalho titulo="Panorama das licitações" />
        <div className="topo-direita">
          <Acompanhamento />
          <Presenca />
        </div>
        <BarraFerramentas ativa={ferramenta} definir={definirFerramenta} />
        <CantoInferior lixeira={lixeira} lixeiraAberta={lixeiraAberta} />
        </div>
        {dialogoEnvio && <DialogoEnviarArquivos iniciais={dialogoEnvio} />}
        {convite && <DialogoAprovarConvite uuid={convite} />}
        {configuracoesAbertas && <TelaConfiguracoes />}
        <AvisoSistema />
        <AvisoNotas />
        {processoUuid && <TelaLicitacao processoUuid={processoUuid} fechar={fechar} nomeQuadro={dados?.quadro.nome ?? 'Quadro'} />}
      </div>
    </ProvedorAcoesEstado>
    </ProvedorConfirmacao>
  );
}

export function TelaQuadro({ usuario }: { usuario: { nome: string; uuid: string } }) {
  const carregar = useQuadro((s) => s.carregar);
  const erro = useQuadro((s) => s.erroCarga);
  useEffect(() => {
    void carregar();
  }, [carregar]);
  const { storeNotas } = useDependencias();
  if (erro) return <main className="pagina-provisoria"><h1>Nexital</h1><p>{erro}</p></main>;
  return (
    <ReactFlowProvider>
      <ProvedorNotas store={storeNotas}>
        <Conteudo usuario={usuario} />
      </ProvedorNotas>
    </ReactFlowProvider>
  );
}
